import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next"; 
import SongSimpliLogo from "./SongSimpliLogo";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getLocalizedPath, isUserLoggedIn } from "../utils/helper";
import ThemeToggle from "./ThemeToggle";
import { ROUTE_CONSTANT } from "../routing/route-constants";
import {
  clearOnboardingSession,
  isOnboardingPageReload,
  loadOnboardingSession,
  saveOnboardingSession,
  type OnboardingSessionData,
} from "../utils/onboardingSession";
import useIsMobile from "../hooks/useIsMobile";

interface OnboardingScreenProps {
  // onSkip: () => void;
}

type SelectionOverrides = {
  recipient?: string | null;
  otherRecipientText?: string;
  recipientName?: string;
  occasion?: string | null;
  otherOccasionText?: string;
  recipientSpecialDescription?: string;
  favoriteMemoriesDescription?: string;
  heartMessageDescription?: string;
  genres?: string | null;
  styles?: string | null;
  tempo?: string | null;
  instruments?: Set<string>;
};
const SONG_RECIPIENTS = [
  "Husband",
  "Wife",
  "Partner",
  "Girlfriend",
  "Boyfriend",
  "Son",
  "Daughter",
  "Fiancé",
  "Father",
  "Mother",
  "Grandparent",
  "Grandson",
  "Granddaughter",
  "Sibling",
  "Friend",
  "Myself",
  "Other",
];

const OTHER_RECIPIENT = "Other";
const OTHER_OCCASION = "Something else...";
const RECIPIENT_OTHER_MAX_CHARS = 300;
const OCCASION_OTHER_MAX_CHARS = 300;
const RECIPIENT_NAME_MAX_CHARS = 40;
const RECIPIENT_SPECIAL_MIN_CHARS = 20;
const RECIPIENT_SPECIAL_MAX_CHARS = 500;
const HEART_MESSAGE_MAX_CHARS = 300;
const TOTAL_ONBOARDING_STEPS = 8;
const TOTAL_ONBOARDING_DISPLAY_STEPS = 9;

const SONG_OCCASIONS = [
  "Just Because",
  "I Love You",
  "Birthday",
  "Anniversary", 
  "Missing You",
  "Fathers Day",
  "Mothers Day",
  "Valentine's Day",
  "Thank You",
  "Apology",
  "Wedding",
  "Friendship",
  "In Memory",
  "Milestone",
  "Proposal",
  "Retirement",
  "Graduation",
  OTHER_OCCASION,
];

const GENRES = [
  "Hip-hop",
  "Country",
  "Electronic",
  "Pop",
  "R&B/Soul",
  "Rock",
  "Jazz",
  "Lo-fi",
  "Afrobeats",
  "Latin",
  "Classical",
  "Folk",
  "Ambient",
];

const STYLES = [
  "Energetic",
  "Chill",
  "Dark",
  "Uplifting",
  "Melancholic",
  "Cinematic",
  "Groovy",
  "Aggressive",
];

const TEMPOS = ["Very slow", "Slow", "Medium", "Fast", "Very fast"];

const INSTRUMENTS = [
  "Guitar",
  "Piano",
  "Drums",
  "Synth",
  "Bass",
  "Strings",
  "Brass",
  "Violin",
];

export default function OnboardingScreen({
  // onSkip
}: OnboardingScreenProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale } = useParams<{ locale?: string }>();
  const [searchParams] = useSearchParams();
  const localeVal = locale ?? "";
  const isFreshStart = searchParams.get("fresh") === "1";
  const [step, setStep] = useState(1);
  const [selectedRecipient, setSelectedRecipient] = useState<string | null>(null);
  const [otherRecipientText, setOtherRecipientText] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [selectedOccasion, setSelectedOccasion] = useState<string | null>(null);
  const [otherOccasionText, setOtherOccasionText] = useState("");
  const [recipientSpecialDescription, setRecipientSpecialDescription] = useState("");
  const [favoriteMemoriesDescription, setFavoriteMemoriesDescription] = useState("");
  const [heartMessageDescription, setHeartMessageDescription] = useState("");
  const [selectedGenres, setSelectedGenres] = useState<string | null>(null);
  const [selectedStyles, setSelectedStyles] = useState<string | null>(null);
  const [selectedTempo, setSelectedTempo] = useState<string | null>(null);
  const [selectedInstruments, setSelectedInstruments] = useState<Set<string>>(new Set());
  const [isDark, setIsDark] = useState(
    () => localStorage.getItem("theme") !== "light",
  );
 const isMobile = useIsMobile();
  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
  }, [isDark]);

  useEffect(() => {
    function resetOnboardingForm() {
      setStep(1);
      setSelectedRecipient(null);
      setOtherRecipientText("");
      setRecipientName("");
      setSelectedOccasion(null);
      setOtherOccasionText("");
      setRecipientSpecialDescription("");
      setFavoriteMemoriesDescription("");
      setHeartMessageDescription("");
      setSelectedGenres(null);
      setSelectedStyles(null);
      setSelectedTempo(null);
      setSelectedInstruments(new Set());
    }

    function applySessionOrClear() {
      if (isFreshStart || isUserLoggedIn() || isOnboardingPageReload()) {
        clearOnboardingSession();
        resetOnboardingForm();
        return;
      }

      const saved = loadOnboardingSession();
      if (!saved) return;

      setSelectedRecipient(saved.selectedRecipient);
      setOtherRecipientText(saved.otherRecipientText);
      setRecipientName(saved.recipientName);
      setSelectedOccasion(saved.selectedOccasion);
      setOtherOccasionText(saved.otherOccasionText);
      setRecipientSpecialDescription(saved.recipientSpecialDescription);
      setFavoriteMemoriesDescription(saved.favoriteMemoriesDescription);
      setHeartMessageDescription(saved.heartMessageDescription);
      setSelectedGenres(saved.selectedGenres[0] ?? null);
      setSelectedStyles(saved.selectedStyles[0] ?? null);
      setSelectedTempo(saved.selectedTempo);
      setSelectedInstruments(new Set(saved.selectedInstruments));
    }

    applySessionOrClear();

    function handlePageShow(event: PageTransitionEvent) {
      if (!event.persisted) return;
      applySessionOrClear();
    }

    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, [isFreshStart]);

  const toggleDark = () => {
    const next = !isDark;
    setIsDark(next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };

  const selectGenre = (genre: string) => {
    setSelectedGenres((prev) => (prev === genre ? null : genre));
  };

  const selectStyle = (style: string) => {
    setSelectedStyles((prev) => (prev === style ? null : style));
  };

  const toggleInstrument = (instrument: string) => {
    setSelectedInstruments((prev) => {
      const next = new Set(prev);
      if (next.has(instrument)) next.delete(instrument);
      else next.add(instrument);
      return next;
    });
  };

  const selectRecipient = (recipient: string) => {
    setSelectedRecipient((prev) => {
      if (prev === recipient) {
        if (recipient === OTHER_RECIPIENT) {
          setOtherRecipientText("");
        }
        return null;
      }
      if (recipient !== OTHER_RECIPIENT) {
        setOtherRecipientText("");
      }
      return recipient;
    });
  };

  const selectOccasion = (occasion: string) => {
    setSelectedOccasion((prev) => {
      if (prev === occasion) {
        if (occasion === OTHER_OCCASION) {
          setOtherOccasionText("");
        }
        return null;
      }
      if (occasion !== OTHER_OCCASION) {
        setOtherOccasionText("");
      }
      return occasion;
    });
  };

  const toggleTempo = (tempo: string) => {
    setSelectedTempo((prev) => (prev === tempo ? null : tempo));
  };

  const buildSessionData = (overrides?: SelectionOverrides): OnboardingSessionData => ({
    selectedRecipient: overrides?.recipient ?? selectedRecipient,
    otherRecipientText: overrides?.otherRecipientText ?? otherRecipientText,
    recipientName: overrides?.recipientName ?? recipientName,
    selectedOccasion: overrides?.occasion ?? selectedOccasion,
    otherOccasionText: overrides?.otherOccasionText ?? otherOccasionText,
    recipientSpecialDescription:
      overrides?.recipientSpecialDescription ?? recipientSpecialDescription,
    favoriteMemoriesDescription:
      overrides?.favoriteMemoriesDescription ?? favoriteMemoriesDescription,
    heartMessageDescription:
      overrides?.heartMessageDescription ?? heartMessageDescription,
    selectedGenres: (overrides?.genres ?? selectedGenres) ? [(overrides?.genres ?? selectedGenres) as string] : [],
    selectedStyles: (overrides?.styles ?? selectedStyles) ? [(overrides?.styles ?? selectedStyles) as string] : [],
    selectedTempo: overrides?.tempo ?? selectedTempo,
    selectedInstruments: [...(overrides?.instruments ?? selectedInstruments)],
  });

  const goToSongSummary = (overrides?: SelectionOverrides) => {
    const sessionData = buildSessionData(overrides);
    saveOnboardingSession(sessionData);
    navigate(
      getLocalizedPath(localeVal, {
        Path: ROUTE_CONSTANT.SONG_SUMMARY,
        LocalePath: ROUTE_CONSTANT.SONG_SUMMARY_LOCALE,
      }),
    );
  };

  const isOtherRecipientOverLimit = otherRecipientText.length > RECIPIENT_OTHER_MAX_CHARS;
  const isStep1ContinueEnabled =
    selectedRecipient !== null &&
    (selectedRecipient !== OTHER_RECIPIENT || otherRecipientText.trim().length > 0) &&
    !isOtherRecipientOverLimit;

  const isRecipientNameOverLimit = recipientName.length > RECIPIENT_NAME_MAX_CHARS;
  const isStep2ContinueEnabled =
    recipientName.trim().length > 0 && !isRecipientNameOverLimit;

  const isOtherOccasionOverLimit = otherOccasionText.length > OCCASION_OTHER_MAX_CHARS;
  const isStep3ContinueEnabled =
    selectedOccasion !== null &&
    (selectedOccasion !== OTHER_OCCASION || otherOccasionText.trim().length > 0) &&
    !isOtherOccasionOverLimit;

  const isStep4ContinueEnabled =
    selectedGenres !== null && selectedStyles !== null;

  const isStep5ContinueEnabled =
    selectedTempo !== null && selectedInstruments.size > 0;

  const isRecipientSpecialOverLimit =
    recipientSpecialDescription.length > RECIPIENT_SPECIAL_MAX_CHARS;
  const isRecipientSpecialUnderMin =
    recipientSpecialDescription.trim().length > 0 &&
    recipientSpecialDescription.trim().length < RECIPIENT_SPECIAL_MIN_CHARS;
  const isStep6ContinueEnabled =
    recipientSpecialDescription.trim().length >= RECIPIENT_SPECIAL_MIN_CHARS &&
    !isRecipientSpecialOverLimit;

  const isFavoriteMemoriesOverLimit =
    favoriteMemoriesDescription.length > RECIPIENT_SPECIAL_MAX_CHARS;
  const isFavoriteMemoriesUnderMin =
    favoriteMemoriesDescription.trim().length > 0 &&
    favoriteMemoriesDescription.trim().length < RECIPIENT_SPECIAL_MIN_CHARS;
  const isStep7ContinueEnabled =
    favoriteMemoriesDescription.trim().length >= RECIPIENT_SPECIAL_MIN_CHARS &&
    !isFavoriteMemoriesOverLimit;

  const isHeartMessageOverLimit = heartMessageDescription.length > HEART_MESSAGE_MAX_CHARS;
  
  const isStep8ContinueEnabled =!isHeartMessageOverLimit;

  const isNavStepContinueEnabled =
    step === 1
      ? isStep1ContinueEnabled
      : step === 2
        ? isStep2ContinueEnabled
        : step === 3
          ? isStep3ContinueEnabled
          : step === 4
            ? isStep4ContinueEnabled
            : step === 5
              ? isStep5ContinueEnabled
              : step === 6
                ? isStep6ContinueEnabled
                : step === 7
                  ? isStep7ContinueEnabled
                  : step === 8
                    ? isStep8ContinueEnabled
                    : true;

  const handleContinue = () => {
    if (step === 1 && !isStep1ContinueEnabled) {
      return;
    }
    if (step === 2 && !isStep2ContinueEnabled) {
      return;
    }
    if (step === 3 && !isStep3ContinueEnabled) {
      return;
    }
    if (step === 4 && !isStep4ContinueEnabled) {
      return;
    }
    if (step === 5 && !isStep5ContinueEnabled) {
      return;
    }
    if (step === 6 && !isStep6ContinueEnabled) {
      return;
    }
    if (step === 7 && !isStep7ContinueEnabled) {
      return;
    }
    if (step === 8 && !isStep8ContinueEnabled) {
      return;
    }
    if (step === TOTAL_ONBOARDING_STEPS) {
      goToSongSummary();
      return;
    }
    if (step < TOTAL_ONBOARDING_STEPS) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const renderNavButtons = (backDisabled: boolean, continueLabel?: string) => (
    <div className="flex w-full items-center justify-between gap-3 sm:gap-6">
      <button
        type="button"
        onClick={handleBack}
        disabled={backDisabled}
        id="back"
        className={`sm:h-auto h-[47px]  uppercase rounded-full px-8 sm:px-16 py-3 sm:py-[20px] text-[#687585] sm:text-[18px] font-bold tracking-[0.36px] transition-colors ${
          backDisabled
            ? isDark
              ? "cursor-not-allowed   border-[#004399] bg-transparent text-[#FDFDFD] "
              : "cursor-not-allowed bg-white text-[#687585]"
            : isDark
              ? "cursor-pointer border-0 border-[#004399] bg-transparent text-[#fdfdfd] hover:text-white"
              : "cursor-pointer bg-white text-[#16283d] hover:text-[#0e76ff]"
        }`}
      >
        {t("onboarding.back")}
      </button> 
      <button
        type="button"
        onClick={handleContinue}
        disabled={!isNavStepContinueEnabled}
        id="continue"
        className={`sm:h-auto h-[47px] uppercase rounded-full px-8 sm:px-16 py-3 sm:py-[20px] sm:text-[18px] font-bold tracking-[0.36px] transition-colors ${
          isNavStepContinueEnabled ? "cursor-pointer text-white" : "cursor-not-allowed text-[#a1a7b0]"
        }`}
        style={
          isNavStepContinueEnabled
            ? { background: "linear-gradient(90deg, #f95a38 0%, #fc792e 50%, #ff9823 100%)"}
            : { background: isDark ? "#EAEBED" : "#eaebed"}
        }
      >
        {continueLabel ?? t("onboarding.continue")}
      </button>
    </div>
  );

  return (
    <div
      className="relative flex h-full min-h-screen flex-col"
      // style={{
      //   background: isDark
      //     ? "linear-gradient(180deg, #0d1833 0%, #1d1245 50%, #2d0f5e 100%)"
      //     : "linear-gradient(180deg, #ffffff 0%, #eee3ff 64%, #b3e2f0 100%)",
      // }}
      style={{
        background: isDark
          ? "radial-gradient(ellipse 80% 65% at 50% 109%, #FF00D633 0%, rgba(255, 0, 214, 0.2) 50%, transparent 100%)"
          : "linear-gradient(180deg, #ffffff 0%, #eee3ff 64%, #b3e2f0 100%)",
      }}
    >
      {/* Nav */}
      <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between sm:py-4 gap-6 sm:gap-20 mb-5`}>
        <div className={`flex items-center justify-between sm:justify-start gap-[4.29px] pb-3 sm:pb-2 p-[10px] sm:px-5 ${isDark ? "border-b border-[#EAEBED] sm:border-b-0" : "border-b border-[#EAEBED] sm:border-b-0"}`}>
          <SongSimpliLogo isDark={isDark} />
          <div className="sm:hidden">
            <ThemeToggle isDark={isDark} onToggle={toggleDark} />
          </div>
        </div>

        <div className="flex flex-1 items-center justify-start gap-3 px-8 sm:px-4">
          <span
            className={`text-[13px] sm:text-[14px] font-normal whitespace-nowrap ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
          >
            {t("onboarding.stepProgress", { current: step, total: TOTAL_ONBOARDING_DISPLAY_STEPS })}
          </span>
          <div className={`h-1.5 w-full sm:max-w-[75%]  rounded-full ${isDark ? "bg-[#264a73]" : "bg-[#d2e5ff]"}`}>
            <div
              className="h-full rounded-full bg-[#0E76FF] transition-all duration-300"
              style={{ width: `${(step / TOTAL_ONBOARDING_DISPLAY_STEPS) * 100}%` }}
            />
          </div>
        </div>

        <div className="hidden sm:block sm:pr-4">
          <ThemeToggle isDark={isDark} onToggle={toggleDark} />
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-1 flex-col items-center sm:justify-center lg:justify-start justify-start sm:pb-0">
        {/* <div className="sm:h-[100%] h-[calc(100vh-80px)] flex flex-col justify-between pb-5 lg:block lg:justify-normal lg:pb-0"> */}
        <div className="h-[calc(100dvh-180px)] sm:h-[calc(100dvh-240px)] 2xl:h-[calc(100dvh-280px)] w-full flex flex-col justify-between pb-5 sm:flex sm:justify-center sm:items-center lg:pb-0">
          <div>
            {step === 1 ? (
              <>
                {selectedRecipient === OTHER_RECIPIENT && isOtherRecipientOverLimit && (
                  
                 <div className="px-2 sm:px-0">
                  <div
                    className={`mx-auto mb-[15px] sm:mb-5 hidden sm:flex w-full  sm:absolute sm:left-1/2 sm:-translate-x-1/2 sm:top-20 lg:top-[60px] 2xl:top-[150px] sm:w-[320px] w-[100%] max-w-170 items-center gap-2 rounded-[10px] border-1 sm:border-2 px-2 py-2 ${
                      isDark
                        ? "border-[#ff4d4f] bg-[#990000] text-[#fdfdfd]"
                        : "border-[#ff4d4f] bg-[#FFE6E6] text-[#DA0000]"
                    }`}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="size-4.5 shrink-0"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8ZM9 12C9 12.5523 8.55229 13 8 13C7.44772 13 7 12.5523 7 12C7 11.4477 7.44772 11 8 11C8.55229 11 9 11.4477 9 12ZM8 3C7.44772 3 7 3.44772 7 4V8C7 8.55228 7.44772 9 8 9C8.55229 9 9 8.55228 9 8V4C9 3.44772 8.55229 3 8 3Z"
                        fill={isDark ? "#FFE6E6" : "#DA0000"}
                      />
                    </svg>
                    <span className="text-[13px] sm:text-[14px] font-normal">
                      {t("onboarding.songRecipient.characterLimitError")}
                    </span>
                  </div>
                  </div>
                )}

                <div className="flex w-full flex-col items-center gap-0 sm:gap-2 sm:px-4 sm:px-0 px-[10px]">
                  <h1
                    id="songRecipientHeading"
                    className={`max-w-[300px] sm:max-w-none text-[20px] sm:text-[36px] lg:text-[44px] mb-1 sm:font-semibold font-bold tracking-[0.88px] leading-normal text-center ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
                  >
                    {t("onboarding.songRecipient.heading")}
                  </h1>
                  <p
                    className={`max-w-[264px] sm:max-w-none text-[12px] sm:text-xl lg:text-[24px] font-normal tracking-[0.48px] text-center ${isDark ? "text-[#FDFDFD]" : "sm:text-[#16283D] text-[]"}`}
                  >
                    {t("onboarding.songRecipient.subheading")}
                  </p>
                </div>

                <div className="flex flex-col items-center sm:mt-[60px] mt-[15px] w-full max-w-180 px-[5px] sm:px-4">
                  {selectedRecipient === OTHER_RECIPIENT && (
                    <div className="relative mb-[15px] sm:mb-5 w-full max-w-170">
                      <input
                        id="otherRecipientText"
                        type="text"
                        value={otherRecipientText}
                        onChange={(e) => setOtherRecipientText(e.target.value)}
                        placeholder={t("onboarding.songRecipient.placeholder")}
                        className={`w-full rounded-[10px] h-[50.2px] border-[1px] sm:border-[2px] pl-3 pl-3 pr-[62px]  sm:pr-[70px] py-3 pr-16 text-[14px] sm:text-[16px] outline-none transition-colors ${
                          isOtherRecipientOverLimit
                            ? isDark
                              ? "border-[#ff4d4f] bg-[#1e3554] text-[#fdfdfd] placeholder:text-[#94a3b8]"
                              : "border-[#ff4d4f] bg-white text-[#16283d] placeholder:text-[#a1a7b0]"
                            : isDark
                              ? "border-[#264A73] bg-[#1C344F] text-[#fdfdfd] placeholder:text-[#94a3b8]"
                              : "border-[#0e76ff] bg-white text-[#16283d] placeholder:text-[#a1a7b0]"
                        }`}
                      />
                      <span
                        className={`absolute right-4 top-1/2 -translate-y-1/2 text-[12px] sm:text-[14px] 
                          ${isOtherRecipientOverLimit ? "text-[#ff4d4f]" : isDark ? "text-[#94a3b8]" : "text-[#a1a7b0]"
                                                    }`}
                      >
                        {otherRecipientText.length}/{RECIPIENT_OTHER_MAX_CHARS}
                      </span>
                    </div>
                  )}

                  <div className="flex w-full flex-wrap justify-center gap-1.5 sm:gap-2">
                    {SONG_RECIPIENTS.map((recipient, index) => {
                      const isSelected = selectedRecipient === recipient;
                      return (
                        <button
                          key={recipient}
                          id={
                            recipient === OTHER_RECIPIENT
                              ? "recipientOther"
                              : `recipient-${index}`
                          }
                          type="button"
                          onClick={() => selectRecipient(recipient)}
                          className={[
                            "cursor-pointer rounded-full sm:px-5 sm:py-1.5 px-[15px] py-[6px] sm:text-[14px] text-[12px] leading-normal transition-colors",
                            isSelected
                              ? isDark
                                ? "border-[1.5px] border-[#0e76ff] bg-[#264a73] font-bold text-[#fdfdfd]"
                                : "border-[1.5px] border-[#0e76ff] bg-[#E7F1FF] font-bold text-[#0e76ff]"
                              : isDark
                                ? "border-[1.5px] border-[#004399] bg-transparent font-normal text-[#fdfdfd]"
                                : "border-[1.5px] border-[#d2e5ff] bg-white font-normal text-[#264A73]",
                          ].join(" ")}
                        >
                          {recipient}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : step === 2 ? (
              <>
                {isRecipientNameOverLimit && (
                  
                 <div className="px-2 sm:px-0">
                  <div
                    className={`mx-auto mb-[15px] sm:mb-5 hidden sm:flex w-full  sm:absolute sm:left-1/2 sm:-translate-x-1/2 sm:top-20 lg:top-[60px] 2xl:top-[150px] sm:w-[320px] w-[100%] max-w-170 items-center gap-2 rounded-[10px] border-1 sm:border-2 px-2 py-2 ${
                      isDark
                        ? "border-[#ff4d4f] bg-[#990000] text-[#fdfdfd]"
                        : "border-[#ff4d4f] bg-[#FFE6E6] text-[#DA0000]"
                    }`}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="size-4.5 shrink-0"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8ZM9 12C9 12.5523 8.55229 13 8 13C7.44772 13 7 12.5523 7 12C7 11.4477 7.44772 11 8 11C8.55229 11 9 11.4477 9 12ZM8 3C7.44772 3 7 3.44772 7 4V8C7 8.55228 7.44772 9 8 9C8.55229 9 9 8.55228 9 8V4C9 3.44772 8.55229 3 8 3Z"
                        fill={isDark ? "#FFE6E6" : "#DA0000"}
                      />
                    </svg>
                    <span className="text-[13px] sm:text-[14px] font-normal">
                      {t("onboarding.songRecipientName.characterLimitError")}
                    </span>
                  </div>
                </div>
                )}

                <div className="flex w-full flex-col items-center gap-0 sm:gap-2 sm:px-4 sm:px-0 px-[10px]">
                  <h1
                    className={`max-w-[300px] sm:max-w-none text-[20px] sm:text-[36px] lg:text-[44px] sm:font-semibold font-bold tracking-[0.88px] leading-normal text-center ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
                  >
                    {t("onboarding.songRecipientName.heading")}
                  </h1>
                  <p
                    className={`max-w-[264px] sm:max-w-none text-[12px] sm:text-xl lg:text-[24px] font-normal tracking-[0.48px] text-center ${isDark ? "text-[#FDFDFD]" : "sm:text-[#16283D] text-[#16283D]"}`}
                  >
                    {t("onboarding.songRecipientName.subheading")}
                  </p>
                </div>

                <div className="flex flex-col items-center sm:mt-[60px] mt-[15px] w-full max-w-180 px-[5px] sm:px-4">
                  <div className="relative w-full max-w-170">
                    <input
                      id="songRecipientName"
                      type="text"
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      placeholder={t("onboarding.songRecipientName.placeholder")}
                      className={`w-full rounded-[10px] h-[50.2px] border-[1px] sm:border-[2px] pl-3 pl-3 pr-[62px]  sm:pr-[70px] py-3 pr-16 text-[14px] sm:text-[16px] outline-none transition-colors ${
                        isRecipientNameOverLimit
                          ? isDark
                            ? "border-[#ff4d4f] bg-[#1e3554] text-[#fdfdfd] placeholder:text-[#94a3b8]"
                            : "border-[#ff4d4f] bg-white text-[#16283d] placeholder:text-[#a1a7b0]"
                          : isDark
                            ? "border-[#264A73] bg-[#1C344F] text-[#fdfdfd] placeholder:text-[#94a3b8]"
                            : "border-[#d2e5ff] bg-white text-[#16283d] placeholder:text-[#a1a7b0]"
                      }`}
                    />
                    <span
                      className={`absolute right-4 top-1/2 -translate-y-1/2 text-[12px] sm:text-[14px] ${
                        isRecipientNameOverLimit
                          ? "text-[#ff4d4f]"
                          : isDark
                            ? "text-[#94a3b8]"
                            : "text-[#a1a7b0]"
                      }`}
                    >
                      {recipientName.length}/{RECIPIENT_NAME_MAX_CHARS}
                    </span>
                  </div>
                </div>
              </>
            ) : step === 3 ? (
              <>
                <div className="flex w-full flex-col items-center gap-0 sm:gap-2 sm:px-4 sm:px-0 px-[10px]">
                  <h1
                    id="songOccasionHeading"
                    className={`max-w-[300px] sm:max-w-none text-[20px] sm:text-[36px] lg:text-[44px] sm:font-semibold font-bold tracking-[0.88px] leading-normal text-center ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
                  >
                    {t("onboarding.songOccasion.heading")}
                  </h1>
                  <p
                    className={`max-w-[264px] sm:max-w-none text-[12px] sm:text-xl lg:text-[24px] font-normal tracking-[0.48px] text-center ${isDark ? "text-[#FDFDFD]" : "sm:text-[#16283D] text-[#16283D]"}`}
                  >
                    {t("onboarding.songOccasion.subheading")}
                  </p>
                </div>

                <div className="flex flex-col items-center sm:mt-[60px] mt-[15px] w-full max-w-180 px-[5px] sm:px-4">
                  {selectedOccasion === OTHER_OCCASION && isOtherOccasionOverLimit && (
                    
                    <div
                      className={`mx-auto mb-[15px] sm:mb-5 hidden sm:flex w-full  sm:absolute sm:left-1/2 sm:-translate-x-1/2 sm:top-20 lg:top-[60px] 2xl:top-[150px] sm:w-[320px] w-[100%] max-w-170 items-center gap-2 rounded-[10px] border-1 sm:border-2 px-2 py-2 ${
                        isDark
                          ? "border-[#ff4d4f] bg-[#990000] text-[#fdfdfd]"
                          : "border-[#ff4d4f] bg-[#FFE6E6] text-[#DA0000]"
                      }`}
                    >
                       <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="size-4.5 shrink-0"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8ZM9 12C9 12.5523 8.55229 13 8 13C7.44772 13 7 12.5523 7 12C7 11.4477 7.44772 11 8 11C8.55229 11 9 11.4477 9 12ZM8 3C7.44772 3 7 3.44772 7 4V8C7 8.55228 7.44772 9 8 9C8.55229 9 9 8.55228 9 8V4C9 3.44772 8.55229 3 8 3Z"
                        fill={isDark ? "#D2E5FF" : "#DA0000"}
                      />
                    </svg>
                      <span className="text-[13px] sm:text-[14px] font-normal">
                        {t("onboarding.songOccasion.characterLimitError")}
                      </span>
                    </div>
                  )}

                  {selectedOccasion === OTHER_OCCASION && (
                    <div className="relative mb-[15px] sm:mb-5 w-full max-w-170">
                      <input
                        id="otherOccasionText"
                        type="text"
                        value={otherOccasionText}
                        onChange={(e) => setOtherOccasionText(e.target.value)}
                        placeholder={t("onboarding.songOccasion.placeholder")}
                        className={`w-full rounded-[10px] h-[50.2px] border-[1px] sm:border-[2px] pl-3 pl-3 pr-[62px]  sm:pr-[70px] py-3 pr-16 text-[14px] sm:text-[16px] outline-none transition-colors ${
                          isOtherOccasionOverLimit
                            ? isDark
                              ? "border-[#ff4d4f] bg-[#1e3554] text-[#fdfdfd] placeholder:text-[#94a3b8]"
                              : "border-[#ff4d4f] bg-white text-[#16283d] placeholder:text-[#a1a7b0]"
                            : isDark
                              ? "border-[#264A73] bg-[#1C344F] text-[#fdfdfd] placeholder:text-[#94a3b8]"
                              : "border-[#0e76ff] bg-white text-[#16283d] placeholder:text-[#a1a7b0]"
                        }`}
                      />
                      <span
                        className={`absolute right-4 top-1/2 -translate-y-1/2 text-[12px] sm:text-[14px] ${
                          isOtherOccasionOverLimit
                            ? "text-[#ff4d4f]"
                            : isDark
                              ? "text-[#94a3b8]"
                              : "text-[#a1a7b0]"
                        }`}
                      >
                        {otherOccasionText.length}/{OCCASION_OTHER_MAX_CHARS}
                      </span>
                    </div>
                  )}

                  <div className="flex w-full flex-wrap justify-center gap-2">
                    {SONG_OCCASIONS.map((occasion, index) => {
                      const isSelected = selectedOccasion === occasion;
                      return (
                        <button
                          key={occasion}
                          id={
                            occasion === OTHER_OCCASION
                              ? "occasionSomethingElse"
                              : `occasion-${index}`
                          }
                          type="button"
                          onClick={() => selectOccasion(occasion)}
                          className={[
                            "cursor-pointer rounded-full sm:px-5 sm:py-1.5 px-[15px] py-[8px] sm:text-[14px] text-[12px] leading-normal transition-colors",
                            isSelected
                              ? isDark
                                ? "border-[1.5px] border-[#0e76ff] bg-[#264a73] font-bold text-[#fdfdfd]"
                                : "border-[1.5px] border-[#0e76ff] bg-white font-bold text-[#0e76ff]"
                              : isDark
                                ? "border-2 border-[#004399] bg-transparent font-normal text-[#fdfdfd]"
                                : "border-2 border-[#d2e5ff] bg-white font-normal text-[#16283d]",
                          ].join(" ")}
                        >
                          {occasion}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : step === 4 ? (
              <>
                {/* Step 4 heading */}
                <div className="flex w-full flex-col items-center gap-1 sm:gap-1 sm:px-4 sm:px-0 px-[10px]">
                  <h1
                    className={`max-w-[300px] sm:max-w-none text-[20px] sm:text-[36px] lg:text-[44px] sm:font-semibold font-bold tracking-[0.88px] leading-normal text-center ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
                  >
                    {t("onboarding.step1.heading")}
                  </h1>
                  <p
                    className={`max-w-[300px] sm:max-w-none text-[12px] sm:text-xl lg:text-[24px] font-normal tracking-[0.48px] text-center ${isDark ? "text-[#FDFDFD]" : "sm:text-[#16283D] text-[#16283D]"}`}
                  >
                    {t("onboarding.step1.subheading")}
                  </p>
                </div>

                <div className="flex flex-col items-center sm:gap-[40px] gap-[15px] sm:mt-[40px] mt-[15px]">
                  {/* Genre section */}
                  <div className="flex flex-col items-center sm:gap-7 gap-[12px]">
                    <span
                      className={`sm:text-[18px] text-[14px] font-bold sm:font-normal tracking-[0.36px] ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
                    >
                      {t("onboarding.step1.genreLabel")}
                    </span>
                    <div className="flex w-full max-w-185 flex-wrap justify-center gap-2 sm:px-4 px-[10px]">
                      {GENRES.map((genre, index) => {
                        const isSelected = selectedGenres === genre;
                        return (
                          <button
                            key={genre}
                            id={`genres-${index}`}
                            onClick={() => selectGenre(genre)}
                            className={[
                              "cursor-pointer rounded-full sm:px-5 sm:py-1.5 px-[15px] py-[8px] sm:text-[14px] text-[12px] leading-normal transition-colors",
                              isSelected
                                ? isDark
                                  ? "border-[1.5px] border-[#0e76ff] bg-[#264a73] font-bold text-[#fdfdfd]"
                                  : "border-[1.5px] border-[#0e76ff] bg-[#e7f1ff] font-bold text-[#0e76ff]"
                                : isDark
                                  ? "border-2 border-[#004399] bg-transparent font-normal text-[#fdfdfd]"
                                  : "border-2 border-[#d2e5ff] bg-white font-normal text-[#16283d]",
                            ].join(" ")}
                          >
                            {genre}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Styles section */}
                  <div className="flex flex-col items-center sm:gap-7 gap-[12px]">
                    <span
                      className={`sm:text-[18px] text-[14px] font-bold sm:font-normal tracking-[0.36px] ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
                    >
                      {t("onboarding.step1.stylesLabel")}
                    </span>
                    <div className="flex w-full max-w-180 flex-wrap justify-center gap-2 sm:px-4 px-[10px]">
                      {STYLES.map((style, index) => {
                        const isSelected = selectedStyles === style;
                        return (
                          <button
                            key={style}
                            id={`style-${index}`}
                            onClick={() => selectStyle(style)}
                            className={[
                              "cursor-pointer rounded-full sm:px-5 sm:py-1.5 px-[15px] py-[8px] sm:text-[14px] text-[12px] leading-normal transition-colors",
                              isSelected
                                ? isDark
                                  ? "border-[1.5px] border-[#0e76ff] bg-[#264a73] font-bold text-[#fdfdfd]"
                                  : "border-[1.5px] border-[#0e76ff] bg-[#e7f1ff] font-bold text-[#0e76ff]"
                                : isDark
                                  ? "border-2 border-[#004399] bg-transparent font-normal text-[#fdfdfd]"
                                  : "border-2 border-[#d2e5ff] bg-white font-normal text-[#16283d]",
                            ].join(" ")}
                          >
                            {style}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </>
            ) : step === 5 ? (
              <>
                {/* Step 5 heading */}
                <div className="flex w-full flex-col items-center gap-0 sm:gap-2 sm:px-4 sm:px-0 px-[10px]">
                  <h1
                    className={`text-[20px] sm:text-[36px] lg:text-[44px] sm:font-semibold font-bold tracking-[0.88px] leading-normal text-left sm:text-center ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
                  >
                    {t("onboarding.step2.heading")}
                  </h1>
                  <p
                    className={`px-12 sm:px-0 max-w-[300px] sm:max-w-none text-[12px] text-center sm:text-xl lg:text-[24px] font-normal tracking-[0.48px] sm:text-left sm:text-center ${isDark ? "text-[#fdfdfd]" : "sm:text-[#16283D] text-[#16283D]"}`}
                  >
                    {t("onboarding.step2.subheading")}
                  </p>
                </div>

                <div className="flex flex-col items-center sm:gap-[40px] gap-[15px] sm:mt-[40px] mt-[15px]">
                  {/* Tempo section */}
                  <div className="flex flex-col items-center sm:gap-7 gap-[12px]">
                    <span
                      className={`sm:text-[18px] text-[14px] font-bold sm:font-normal tracking-[0.36px] ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
                    >
                      {t("onboarding.step2.tempoLabel")}
                    </span>
                    <div className="flex flex-wrap justify-center sm:justify-start gap-2 px-[10px] sm:px-0">
                      {TEMPOS.map((tempo, index) => {
                        const isSelected = selectedTempo === tempo;
                        return (
                          <button
                            key={tempo}
                            id={`tempo-${index}`}
                            onClick={() => toggleTempo(tempo)}
                            className={[
                              "cursor-pointer rounded-full sm:px-5 sm:py-1.5 px-[15px] py-[8px] sm:text-[14px] text-[12px] leading-normal transition-colors",
                              isSelected
                                ? isDark
                                  ? "border-[1.5px] border-[#0e76ff] bg-[#264a73] font-bold text-[#fdfdfd]"
                                  : "border-[1.5px] border-[#0e76ff] bg-[#e7f1ff] font-bold text-[#0e76ff]"
                                : isDark
                                  ? "border-2 border-[#004399] bg-transparent font-normal text-[#fdfdfd]"
                                  : "border-2 border-[#d2e5ff] bg-white font-normal text-[#16283d]",
                            ].join(" ")}
                          >
                            {tempo}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Instrument section */}
                  <div className="flex flex-col items-center sm:gap-7 gap-[12px]">
                    <span
                      className={`sm:text-[18px] text-[14px] font-bold sm:font-normal tracking-[0.36px] ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
                    >
                      {t("onboarding.step2.instrumentLabel")}
                    </span>
                    <div className="flex w-full max-w-140 flex-wrap justify-center gap-2 px-4 sm:px-0">
                      {INSTRUMENTS.map((instrument, index) => {
                        const isSelected = selectedInstruments.has(instrument);
                        return (
                          <button
                            key={instrument}
                            id={`instrument-${index}`}
                            onClick={() => toggleInstrument(instrument)}
                            className={[
                              "cursor-pointer rounded-full sm:px-5 sm:py-1.5 px-[15px] py-[8px] sm:text-[14px] text-[12px] leading-normal transition-colors",
                              isSelected
                                ? isDark
                                  ? "border-[1.5px] border-[#0e76ff] bg-[#264a73] font-bold text-[#fdfdfd]"
                                  : "border-[1.5px] border-[#0e76ff] bg-[#e7f1ff] font-bold text-[#0e76ff]"
                                : isDark
                                  ? "border-2 border-[#004399] bg-transparent font-normal text-[#fdfdfd]"
                                  : "border-2 border-[#d2e5ff] bg-white font-normal text-[#16283d]",
                            ].join(" ")}
                          >
                            {instrument}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </>
            ) : step === 6 ? (
              <>
                {isRecipientSpecialOverLimit && (
                  
                 <div className="px-6 sm:px-0">
                  <div
                    className={`mx-auto mb-[15px] sm:mb-5 flex sm:flex sm:absolute sm:left-1/2 sm:-translate-x-1/2 sm:top-20 lg:top-[60px] 2xl:top-[150px] sm:w-[320px] w-[100%]  max-w-170 items-center gap-2 rounded-[10px] border-1 sm:border-2 px-2 py-2 ${
                      isDark
                        ? "border-[#ff4d4f] bg-[#990000] text-[#fdfdfd]"
                        : "border-[#ff4d4f] bg-[#FFE6E6] text-[#DA0000]"
                    }`}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="size-4.5 shrink-0"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8ZM9 12C9 12.5523 8.55229 13 8 13C7.44772 13 7 12.5523 7 12C7 11.4477 7.44772 11 8 11C8.55229 11 9 11.4477 9 12ZM8 3C7.44772 3 7 3.44772 7 4V8C7 8.55228 7.44772 9 8 9C8.55229 9 9 8.55228 9 8V4C9 3.44772 8.55229 3 8 3Z"
                        fill={isDark ? "#FFE6E6" : "#DA0000"}
                      />
                    </svg>
                    <span className="text-[13px] sm:text-[14px] font-normal">
                      {t("onboarding.recipientSpecial.characterLimitError")}
                    </span>
                  </div>
                  </div>
                )}

                {isRecipientSpecialUnderMin && !isRecipientSpecialOverLimit && (
                  
                 <div className="px-6 sm:px-0">
                  <div
                    className={`mx-auto mb-[15px] sm:mb-5 flex sm:flex sm:absolute sm:left-1/2 sm:-translate-x-1/2 sm:top-20 lg:top-[60px] 2xl:top-[150px] sm:w-[320px] w-[100%] max-w-170 items-center gap-2 rounded-[10px] border-1 sm:border-2 px-2 py-2 ${
                      isDark
                        ? "border-[#0e76ff] bg-[#004399] text-[#fdfdfd]"
                        : "border-[#0e76ff] bg-[#e7f1ff] text-[#0E76FF]"
                    }`}
                  >
                     <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="size-4.5 shrink-0"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8ZM9 12C9 12.5523 8.55229 13 8 13C7.44772 13 7 12.5523 7 12C7 11.4477 7.44772 11 8 11C8.55229 11 9 11.4477 9 12ZM8 3C7.44772 3 7 3.44772 7 4V8C7 8.55228 7.44772 9 8 9C8.55229 9 9 8.55228 9 8V4C9 3.44772 8.55229 3 8 3Z"
                        fill={isDark ? "#D2E5FF" : "#0E76FF"}
                      />
                    </svg>
                    <span className="text-[13px] sm:text-[14px] font-normal">
                      {t("onboarding.recipientSpecial.minCharactersError", {
                        count: recipientSpecialDescription.trim().length,
                      })}
                    </span>
                  </div>
                  </div>
                )}

                <div className="flex w-full flex-col items-center gap-0 sm:gap-2 sm:px-4 sm:px-0 px-[10px]">
                  <h1
                    className={`max-w-[300px] sm:max-w-none text-[20px] sm:text-[36px] lg:text-[44px] sm:font-semibold font-bold tracking-[0.88px] leading-normal text-center ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
                  >
                    {t("onboarding.recipientSpecial.heading")}
                  </h1>
                  <p
                    className={`max-w-[220px] sm:max-w-none text-[12px] sm:text-xl lg:text-[24px] font-normal tracking-[0.48px] text-center ${isDark ? "text-[#FDFDFD]" : "sm:text-[#16283D] text-[#16283D]"}`}
                  >
                    {t("onboarding.recipientSpecial.subheading")}
                  </p>
                  <p
                    className={`max-w-[318px] sm:max-w-none text-[12px] sm:text-[18px] font-normal text-center ${isDark ? "text-[#FFFFFF]" : "text-[#687585]"}`}
                  >
                    {t("onboarding.recipientSpecial.hint")}
                  </p>
                </div>

                <div className="flex flex-col items-center sm:mt-[40px] mt-[15px] w-full px-[10px] sm:px-4">
                  <div className={`relative w-full max-w-170  rounded-[10px] border-[2px]  pb-6 ${ isRecipientSpecialOverLimit
                          ? isDark
                            ? "border-[#ff4d4f] bg-[#1e3554] text-[#fdfdfd] placeholder:text-[#94a3b8]"
                            : "border-[#ff4d4f] bg-white text-[#16283D ] placeholder:text-[#a1a7b0]"
                          : isDark
                            ? "border-[#264A73] bg-[#1C344F] text-[#fdfdfd] placeholder:text-[#fff]"
                            : "border-[#d2e5ff] bg-white text-[#16283D ] placeholder:text-[#a1a7b0]"
                      }`}>
                    <textarea
                      id="recipientSpecialDescription"
                      value={recipientSpecialDescription}
                      onChange={(e) => setRecipientSpecialDescription(e.target.value)}
                      placeholder={t("onboarding.recipientSpecial.placeholderDesktop")}
                      rows={isMobile ? 10 : 6}
                      className={`w-full resize-none leading-6 px-4 py-3 max-h-[150px] overflow-auto pb-8 text-[12px] sm:text-[16px] outline-none transition-colors ${isDark ? "text-[#FDFDFD]" : "#16283D" }`}
                    />
                    <span
                      className={`absolute bottom-3 right-4 text-[12px] sm:text-[14px] ${
                        isRecipientSpecialOverLimit
                          ? "text-[#ff4d4f]"
                          : isDark
                            ? "text-[#fdfdfd]"
                            : "text-[#a1a7b0]"
                      }`}
                    >
                      {recipientSpecialDescription.length}/{RECIPIENT_SPECIAL_MAX_CHARS}
                    </span>
                  </div>
                </div>
              </>
            ) : step === 7 ? (
              <>
                {isFavoriteMemoriesOverLimit && (
                  
                 <div className="px-6 sm:px-0">
                  <div
                    className={`mx-auto mb-[15px] sm:mb-5 flex sm:flex sm:absolute sm:left-1/2 sm:-translate-x-1/2 sm:top-20 lg:top-[60px] 2xl:top-[150px] sm:w-[320px] w-[100%] max-w-170 items-center gap-2 rounded-[10px] border-1 sm:border-2 px-2 py-2 ${
                      isDark
                        ? "border-[#ff4d4f] bg-[#990000] text-[#fdfdfd]"
                        : "border-[#ff4d4f] bg-[#FFE6E6] text-[#DA0000]"
                    }`}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="size-4.5 shrink-0"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8ZM9 12C9 12.5523 8.55229 13 8 13C7.44772 13 7 12.5523 7 12C7 11.4477 7.44772 11 8 11C8.55229 11 9 11.4477 9 12ZM8 3C7.44772 3 7 3.44772 7 4V8C7 8.55228 7.44772 9 8 9C8.55229 9 9 8.55228 9 8V4C9 3.44772 8.55229 3 8 3Z"
                        fill={isDark ? "#FFE6E6" : "#DA0000"}
                      />
                    </svg>
                    <span className="text-[13px] sm:text-[14px] font-normal">
                      {t("onboarding.favoriteMemories.characterLimitError")}
                    </span>
                  </div>
                 </div>
                )}

                {isFavoriteMemoriesUnderMin && !isFavoriteMemoriesOverLimit && (
                  
                 <div className="px-6 sm:px-0">
                  <div
                    className={`mx-auto mb-[15px] sm:mb-5 flex sm:flex sm:absolute sm:left-1/2 sm:-translate-x-1/2 sm:top-20 lg:top-[60px] 2xl:top-[150px] sm:w-[320px] w-[100%] max-w-170 items-center gap-2 rounded-[10px] border-1 sm:border-2 px-2 py-2 ${
                      isDark
                        ? "border-[#0e76ff] bg-[#004399] text-[#fdfdfd]"
                        : "border-[#0e76ff] bg-[#e7f1ff] text-[#0E76FF]"
                    }`}
                  >
                     <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="size-4.5 shrink-0"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8ZM9 12C9 12.5523 8.55229 13 8 13C7.44772 13 7 12.5523 7 12C7 11.4477 7.44772 11 8 11C8.55229 11 9 11.4477 9 12ZM8 3C7.44772 3 7 3.44772 7 4V8C7 8.55228 7.44772 9 8 9C8.55229 9 9 8.55228 9 8V4C9 3.44772 8.55229 3 8 3Z"
                        fill={isDark ? "#D2E5FF" : "#0E76FF"}
                      />
                    </svg>
                    <span className="text-[13px] sm:text-[14px] font-normal">
                      {t("onboarding.favoriteMemories.minCharactersError", {
                        count: favoriteMemoriesDescription.trim().length,
                      })}
                    </span>
                  </div>
                  </div>
                )}

                <div className="flex w-full flex-col items-center gap-0 sm:gap-2 sm:px-4 sm:px-0 px-[10px]">
                  <h1
                    className={`max-w-[350px] sm:max-w-none text-2xl  sm:text-[36px] lg:text-[44px] sm:font-semibold font-bold tracking-[0.88px] leading-normal text-center ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
                  >
                    {t("onboarding.favoriteMemories.heading")}
                  </h1>
                  <p
                    className={`max-w-[318px] sm:max-w-none text-[12px] sm:text-xl lg:text-[24px] font-normal tracking-[0.48px] text-center ${isDark ? "text-[#FDFDFD]" : "sm:text-[#16283D] text-[#16283D]"}`}
                  >
                    {t("onboarding.favoriteMemories.subheading")}
                  </p>
                  <p
                    className={`max-w-[318px] sm:max-w-none text-[12.0px] sm:text-[18px] font-normal tracking-[0.28px] text-center ${isDark ? "text-[#FDFDFD]" : "text-[#687585]"}`}
                  >
                    {t("onboarding.favoriteMemories.hint")}
                  </p>
                </div>

                <div className="flex flex-col items-center sm:mt-[60px] mt-[15px] w-full max-w-180 px-[5px] sm:px-4">
                   <div className={`relative w-full max-w-170  rounded-[10px] border-[2px]  pb-6 ${ isFavoriteMemoriesOverLimit
                          ? isDark
                            ? "border-[#ff4d4f] bg-[#1e3554] text-[#fdfdfd] placeholder:text-[#94a3b8]"
                            : "border-[#ff4d4f] bg-white text-[#16283D ] placeholder:text-[#a1a7b0]"
                          : isDark
                            ? "border-[#264A73] bg-[#1C344F] text-[#fdfdfd] placeholder:text-[#fff]"
                            : "border-[#d2e5ff] bg-white text-[#16283D ] placeholder:text-[#a1a7b0]"
                      }`}>
                    <textarea
                      id="favoriteMemoriesDescription"
                      value={favoriteMemoriesDescription}
                      onChange={(e) => setFavoriteMemoriesDescription(e.target.value)}
                      placeholder={t("onboarding.favoriteMemories.placeholder")}
                      rows={isMobile ? 10 : 6}
                     className={`w-full resize-none leading-6 px-4 py-3 max-h-[150px] overflow-auto pb-8 text-[12px] sm:text-[16px] outline-none transition-colors ${isDark ? "text-[#FDFDFD]" : "#16283D" }`}
                    />
                    <span
                      className={`absolute bottom-3 right-4 text-[12px] sm:text-[14px] ${
                        isFavoriteMemoriesOverLimit
                          ? "text-[#ff4d4f]"
                          : isDark
                            ? "text-[#94a3b8]"
                            : "text-[#a1a7b0]"
                      }`}
                    >
                      {favoriteMemoriesDescription.length}/{RECIPIENT_SPECIAL_MAX_CHARS}
                    </span>
                  </div>
                </div>
              </>
            ) : step === 8 ? (
              <>
                {isHeartMessageOverLimit && (
                  
                 <div className="px-6 sm:px-0">
                  <div
                    className={`mx-auto mb-[15px] sm:mb-5 flex sm:flex sm:absolute sm:left-1/2 sm:-translate-x-1/2 sm:top-20 lg:top-[60px] 2xl:top-[150px] sm:w-[320px] w-[100%] max-w-170 items-center gap-2 rounded-[10px] border-1 sm:border-2 px-2 py-2 ${
                      isDark
                        ? "border-[#ff4d4f] bg-[#990000] text-[#fdfdfd]"
                        : "border-[#ff4d4f] bg-[#FFE6E6] text-[#DA0000]"
                    }`}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="size-4.5 shrink-0"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8ZM9 12C9 12.5523 8.55229 13 8 13C7.44772 13 7 12.5523 7 12C7 11.4477 7.44772 11 8 11C8.55229 11 9 11.4477 9 12ZM8 3C7.44772 3 7 3.44772 7 4V8C7 8.55228 7.44772 9 8 9C8.55229 9 9 8.55228 9 8V4C9 3.44772 8.55229 3 8 3Z"
                        fill={isDark ? "#FFE6E6" : "#DA0000"}
                      />
                    </svg>
                    <span className="text-[13px] sm:text-[14px] font-normal">
                      {t("onboarding.heartMessage.characterLimitError")}
                    </span>
                  </div>
                  </div>
                )}

                <div className="flex w-full flex-col items-center gap-0 sm:gap-2 sm:px-4 sm:px-0 px-[10px]">
                  <h1
                    className={`max-w-[300px] sm:max-w-none text-[20px] sm:text-[36px] lg:text-[44px] sm:font-semibold font-bold tracking-[0.88px] leading-normal text-center ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
                  >
                    {t("onboarding.heartMessage.heading")}
                  </h1>
                  <p
                    className={`max-w-[318px] sm:max-w-none text-[12px] sm:text-xl lg:text-[24px] font-normal tracking-[0.48px] text-center ${isDark ? "text-[#FDFDFD]" : "sm:text-[#16283D] text-[#16283D]"}`}
                  >
                    {t("onboarding.heartMessage.subheading")}
                  </p>
                  <p
                    className={`max-w-[318px] sm:max-w-none text-[12px] sm:text-[18px] font-normal text-center ${isDark ? "text-[#FDFDFD]" : "text-[#687585]"}`}
                  >
                    {t("onboarding.heartMessage.hint")}
                  </p>
                </div>

                <div className="flex flex-col items-center sm:mt-[60px] mt-[15px] w-full max-w-180 px-[5px] sm:px-4">
                   <div className={`relative w-full max-w-170  rounded-[10px] border-[2px]  pb-6 ${ isHeartMessageOverLimit
                          ? isDark
                            ? "border-[#ff4d4f] bg-[#1e3554] text-[#fdfdfd] placeholder:text-[#94a3b8]"
                            : "border-[#ff4d4f] bg-white text-[#16283D ] placeholder:text-[#a1a7b0]"
                          : isDark
                            ? "border-[#264A73] bg-[#1C344F] text-[#fdfdfd] placeholder:text-[#fff]"
                            : "border-[#d2e5ff] bg-white text-[#16283D ] placeholder:text-[#a1a7b0]"
                      }`}>
                    <textarea
                      id="heartMessageDescription"
                      value={heartMessageDescription}
                      onChange={(e) => setHeartMessageDescription(e.target.value)}
                      placeholder={t("onboarding.heartMessage.placeholder")}
                      rows={isMobile ? 10 : 6}
                      className={`w-full resize-none leading-6 px-4 py-3 max-h-[150px] overflow-auto pb-8 text-[12px] sm:text-[16px] outline-none transition-colors ${isDark ? "text-[#FDFDFD]" : "#16283D" }`}
                    />
                    <span
                      className={`absolute bottom-3 right-4 text-[12px] sm:text-[14px] ${
                        isHeartMessageOverLimit
                          ? "text-[#ff4d4f]"
                          : isDark
                            ? "text-[#94a3b8]"
                            : "text-[#a1a7b0]"
                      }`}
                    >
                      {heartMessageDescription.length}/{HEART_MESSAGE_MAX_CHARS}
                    </span>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
        <div className="flex flex-col items-center sm:mt-10 w-[94%] sm:w-auto sm:mb-0 mb-[10px] mx-auto mt-[15px] sm:mx-0 sm:max-w-[1100px] sm:w-full">
            {renderNavButtons(
              step === 1,
              step === 8 ? t("onboarding.next") : undefined,
            )}
        </div>


      </div>
    </div>
  );
}
